import { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import "./promoCodeList.style.scss";
import deleteIcon from "../../../images/DeleteIcon.svg";
import editIcon from "../../../images/EditIcon.svg";
import addIcon from "../../../images/Plus.svg";
import { useAlert } from "react-alert";
import ConfirmationModal from "./PromoCodeModal";
import PromoCodeAddEditModal from "./PromoCodeAddEditModal";
import { db } from "../../../firebase";
import ENV from "../../../env";
import { collection, getDocs } from "firebase/firestore";
import {
  deleteTicketingPromoCode,
  saveTicketingPromoCode,
} from "../../../utility/saveTicketingPromoCode";

export default function PromoCodeList({ eventId, orgId, tickets, onPromoCodesChanged }) {
  const { t } = useTranslation("ticketingDashboard");
  const alert = useAlert();

  const [promoCodes, setPromoCodes] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPromoCodeAddEditModal, setShowPromoCodeAddEditModal] = useState(false);
  const [selectedPromoCode, setSelectedPromoCode] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [visibleCount, setVisibleCount] = useState(50);
  const [isLoading, setIsLoading] = useState(false);

  const ITEMS_PER_LOAD = 20;
  const ROW_HEIGHT = 60;

  useEffect(() => {
    if (eventId) {
      fetchPromoCodes();
    }
  }, [eventId]);

  const fetchPromoCodes = async () => {
    try {
      const promoCodesRef = collection(db, "Events", eventId, "PromoCodes");
      const snapshot = await getDocs(promoCodesRef);
      const promoCodesData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setPromoCodes(promoCodesData);
    } catch (error) {
      console.error("Error fetching promo codes:", error);
      alert.error(t("promo.fetchError"));
    }
  };

  const getTicketName = (ticketId) => {
    const ticket = tickets.find((t) => t.id === ticketId);
    return ticket ? ticket.name : t("promo.unknownTicket");
  };

  const getTicketDiscountsDisplay = (promoCode) => {
    if (!promoCode.ticketDiscounts || promoCode.ticketDiscounts.length === 0) {
      return <span className="no-discounts">{t("promo.noDiscounts")}</span>;
    }

    return (
      <div className="ticket-discounts">
        {promoCode.ticketDiscounts.map((discount) => {
          const ticketName = getTicketName(discount.ticketId);
          return (
            <span key={discount.ticketId} className="discount-badge">
              {ticketName}: {discount.discountValue ?? discount.discountPercentage}
              {discount.isPercentage === false ? "€" : "%"}
            </span>
          );
        })}
      </div>
    );
  };

  const getUsageDisplay = (promoCode) => {
    if (promoCode.maxUsage === 0) {
      return `${promoCode.currentUsage} / ${t("promo.unlimited")}`;
    }
    return `${promoCode.currentUsage} / ${promoCode.maxUsage}`;
  };

  const getStatusBadge = (promoCode) => {
    if (promoCode.isActive) {
      return <span className="status-badge active">{t("promo.active")}</span>;
    }
    return <span className="status-badge inactive">{t("promo.inactive")}</span>;
  };

  const handleDeleteClick = (promoCode) => {
    setSelectedPromoCode(promoCode);
    setShowDeleteModal(true);
  };

  const handleEditPromoCodeModal = (promoCode) => {
    setSelectedPromoCode(promoCode);
    setShowPromoCodeAddEditModal(true);
  };

  const onClosePromoCodeModal = () => {
    setShowPromoCodeAddEditModal(false);
    setSelectedPromoCode(null);
  };

  const handleCreatePromoCode = () => {
    setSelectedPromoCode(null);
    setShowPromoCodeAddEditModal(true);
  };

  const handleSavePromoCode = async (promoCodeData) => {
    try {
      setIsLoading(true);
      await saveTicketingPromoCode({ eventId, orgId, promoCode: promoCodeData });
      await fetchPromoCodes();
      await onPromoCodesChanged?.();
      alert.success(t(promoCodeData.id ? "promo.promoCodeUpdated" : "promo.promoCodeCreated"));

      setShowPromoCodeAddEditModal(false);
      setSelectedPromoCode(null);
    } catch (error) {
      console.error("Error saving promo code:", error);
      alert.error(error.message || t("promo.saveError"));
    } finally {
      setIsLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (selectedPromoCode) {
      try {
        setIsLoading(true);
        await deleteTicketingPromoCode({ eventId, orgId, promoCodeId: selectedPromoCode.id });
        await fetchPromoCodes();
        await onPromoCodesChanged?.();
        alert.success(t("promo.promoCodeDeleted"));
        setShowDeleteModal(false);
        setSelectedPromoCode(null);
      } catch (error) {
        console.error("Error deleting promo code:", error);
        alert.error(error.message || t("promo.deleteError"));
      } finally {
        setIsLoading(false);
      }
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setSelectedPromoCode(null);
  };

  const filteredPromoCodes = useMemo(() => {
    if (!searchTerm) return promoCodes;

    return promoCodes.filter(
      (promoCode) =>
        promoCode.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        promoCode.code.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [promoCodes, searchTerm]);

  const visiblePromoCodes = useMemo(() => {
    return filteredPromoCodes.slice(0, visibleCount);
  }, [filteredPromoCodes, visibleCount]);

  const hasMore = visibleCount < filteredPromoCodes.length;

  const handleSearch = useCallback((e) => {
    setSearchTerm(e.target.value);
    setVisibleCount(ITEMS_PER_LOAD); // Reset to initial load when searching
  }, []);

  const loadMore = useCallback(() => {
    if (!isLoading && hasMore) {
      setIsLoading(true);
      setTimeout(() => {
        setVisibleCount((prev) => prev + ITEMS_PER_LOAD);
        setIsLoading(false);
      }, 300); // Small delay to simulate loading
    }
  }, [isLoading, hasMore]);

  // Intersection Observer for infinite scroll
  const observerRef = useCallback(
    (node) => {
      if (isLoading) return;
      if (node) {
        const observer = new IntersectionObserver(
          (entries) => {
            if (entries[0].isIntersecting && hasMore) {
              loadMore();
            }
          },
          { threshold: 1.0 },
        );
        observer.observe(node);
        return () => observer.disconnect();
      }
    },
    [isLoading, hasMore, loadMore],
  );

  const renderPromoCodeRow = useCallback(
    (promoCode, index) => (
      <tr key={promoCode.id} style={{ height: ROW_HEIGHT }}>
        <td className="name-cell">{promoCode.name}</td>
        <td className="code-cell">
          <code className="promo-code-display">{promoCode.code}</code>
        </td>
        <td className="discounts-cell">{getTicketDiscountsDisplay(promoCode)}</td>
        <td className="usage-cell">{getUsageDisplay(promoCode)}</td>
        <td className="status-cell">{getStatusBadge(promoCode)}</td>
        <td className="actions-cell">
          {promoCode.isPartnerCode ? (
            <div className="action-buttons">
              <a
                href={`${ENV.hostname}/events/${eventId}/partner/${promoCode.id}`}
                target="_blank"
                className="partner-btn"
              >
                {t("goToSale")}
              </a>
            </div>
          ) : (
            <p className="no-partner-text">{t("notPartnerCode")}</p>
          )}
        </td>

        <td className="actions-cell">
          <div className="action-buttons">
            <button
              className="action-btn"
              title={t("Edit", { name: promoCode.name })}
              onClick={() => handleEditPromoCodeModal(promoCode)}
            >
              <img src={editIcon} alt={t("Edit", { name: promoCode.name })} />
            </button>
            <button
              className="action-btn"
              title={t("delete")}
              onClick={() => handleDeleteClick(promoCode)}
            >
              <img src={deleteIcon} alt={t("delete")} />
            </button>
          </div>
        </td>
      </tr>
    ),
    [
      handleEditPromoCodeModal,
      handleDeleteClick,
      getTicketDiscountsDisplay,
      getUsageDisplay,
      getStatusBadge,
      t,
      editIcon,
      deleteIcon,
      ROW_HEIGHT,
    ],
  );

  return (
    <div className="promo-code-list">
      <div className="themeCard themeTable br-10 border-transparent">
        <div className="card-header pt-3">
          <div className="header-content">
            <h6 className="m-0 tableHeading">{t("promo.promoCodeList")}</h6>
            <div className="header-actions">
              <div className="search-container">
                <input
                  type="text"
                  placeholder={t("promo.searchPlaceholder") || "Search promo codes..."}
                  value={searchTerm}
                  onChange={handleSearch}
                  className="form-control search-input"
                />
              </div>
              <button
                className="create-promo-btn"
                onClick={handleCreatePromoCode}
                title={t("promo.createPromoCode")}
              >
                <img src={addIcon} alt={t("promo.createPromoCode")} />
                <span>{t("promo.createPromoCode")}</span>
              </button>
            </div>
          </div>
        </div>

        <div className="card-body p-sm-3 pt-0 pt-sm-0">
          {promoCodes.length === 0 ? (
            <div className="empty-state">
              <p>{t("promo.noPromoCodes")}</p>
            </div>
          ) : (
            <>
              <div className="virtualized-table-container">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th scope="col">{t("promo.name")}</th>
                      <th scope="col">{t("promo.code")}</th>
                      <th scope="col">{t("promo.ticketDiscounts")}</th>
                      <th scope="col">{t("promo.usage")}</th>
                      <th scope="col">{t("promo.status")}</th>
                      <th scope="col">{t("partnerLink")}</th>
                      <th scope="col">{t("promo.actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visiblePromoCodes.map((promoCode, index) =>
                      renderPromoCodeRow(promoCode, index),
                    )}
                  </tbody>
                </table>
                {hasMore && (
                  <div className="load-more-container">
                    <div ref={observerRef} className="load-more-trigger">
                      {isLoading ? (
                        <div className="loading-spinner">
                          <span>{t("promo.loading") || "Loading more..."}</span>
                        </div>
                      ) : (
                        <button className="load-more-btn" onClick={loadMore}>
                          {t("promo.loadMore") || "Load More"}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <PromoCodeAddEditModal
        isOpen={showPromoCodeAddEditModal}
        onClose={onClosePromoCodeModal}
        onSave={handleSavePromoCode}
        promoCode={selectedPromoCode}
        tickets={tickets}
        isSaving={isLoading}
      />
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={cancelDelete}
        title={t("promo.confirmDelete")}
        message={t("promo.confirmDeleteMessage", { name: selectedPromoCode?.name })}
        cancelAction={{
          label: t("cancel"),
          className: "cancel-btn",
        }}
        confirmAction={{
          label: t("delete"),
          className: "danger-btn",
          onClick: confirmDelete,
        }}
      />
    </div>
  );
}
